import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pause, Play, Trash2, Save, Loader2, MapPin, Clock, Settings as SettingsIcon, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import useSEO from "@/lib/seo";
import Time24Picker from "@/components/Time24Picker";

const WEEKDAYS = [
  { value: 1, short: "Mo", long: "Montag" },
  { value: 2, short: "Di", long: "Dienstag" },
  { value: 3, short: "Mi", long: "Mittwoch" },
  { value: 4, short: "Do", long: "Donnerstag" },
  { value: 5, short: "Fr", long: "Freitag" },
  { value: 6, short: "Sa", long: "Samstag" },
  { value: 7, short: "So", long: "Sonntag" },
];

function statusLabel(s) {
  return {
    pending: "Noch nicht bestätigt",
    active: "Aktiv",
    paused: "Pausiert",
    unsubscribed: "Abgemeldet",
    deleted_request: "Löschung angefragt",
  }[s] || s;
}

export default function BerufsverkehrTickerVerwalten() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);

  useSEO({
    title: "Berufsverkehr-Ticker verwalten",
    description: "Verwalten Sie Ihren Berufsverkehr-Ticker — pausieren, anpassen oder löschen.",
    path: `/berufsverkehr-ticker/verwalten`,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/commuter/manage/${encodeURIComponent(token)}`);
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const toggleDay = (d) =>
    setData((x) => ({
      ...x,
      weekdays: x.weekdays.includes(d) ? x.weekdays.filter((y) => y !== d) : [...x.weekdays, d].sort(),
    }));

  const saveChanges = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/commuter/manage/${encodeURIComponent(token)}/update`, {
        first_name: data.first_name,
        last_name: data.last_name,
        start_address: data.start_address,
        destination_address: data.destination_address,
        departure_time: data.departure_time,
        weekdays: data.weekdays,
      });
      setData(r.data);
      toast.success("Änderungen gespeichert.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const doAction = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await api.post(`/commuter/manage/${encodeURIComponent(token)}/${action}`);
      await load();
      toast.success("Aktualisiert.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (notFound) {
    return (
      <div className="container-narrow py-16 max-w-3xl" data-testid="commuter-manage-not-found">
        <h1 className="font-semibold text-3xl mb-3">Verwaltungslink ungültig</h1>
        <p className="text-neutral-600 mb-6">Der Link ist abgelaufen oder die Anmeldung wurde bereits gelöscht.</p>
        <Link to="/berufsverkehr-ticker" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-3 rounded-full text-sm">Neu anmelden</Link>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="container-narrow py-20 text-center text-neutral-500" data-testid="commuter-manage-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" /> Lädt…
      </div>
    );
  }

  const disabled = data.status === "unsubscribed" || data.status === "deleted_request";

  return (
    <div data-testid="commuter-manage-page">
      <section className="relative bg-neutral-50 overflow-hidden">
        <div className="container-narrow pt-14 pb-12 relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-5">
            <SettingsIcon className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-dark))]">Berufsverkehr-Ticker · Verwaltung</span>
          </div>
          <h1 className="font-semibold text-3xl sm:text-4xl mb-2" data-testid="commuter-manage-greeting">
            Hallo {data.first_name} {data.last_name}
          </h1>
          <p className="text-neutral-600 text-base mb-5">
            Hier können Sie Ihre Route und Abfahrtszeit anpassen, den Service pausieren oder vollständig abmelden.
          </p>
          <span
            className={`inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] px-3 py-1.5 border ${
              data.status === "active"
                ? "bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-green-dark))] border-[hsl(var(--brand-green))]"
                : data.status === "paused"
                ? "bg-amber-50 text-amber-800 border-amber-300"
                : data.status === "pending"
                ? "bg-neutral-100 text-neutral-700 border-neutral-300"
                : "bg-red-50 text-red-800 border-red-300"
            }`}
            data-testid="commuter-manage-status"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" /> Status: {statusLabel(data.status)}
          </span>
        </div>
      </section>

      <section>
        <div className="container-narrow py-12 max-w-3xl space-y-8">

          {data.status === "pending" && (
            <div className="border border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-3 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 mt-0.5" />
              <div>Ihre Anmeldung ist noch nicht bestätigt. Bitte prüfen Sie Ihr E-Mail-Postfach.</div>
            </div>
          )}

          {/* Aktionen */}
          <div className="bg-white border border-brand-line p-6 sm:p-8">
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-dark))] mb-3">Schnellaktionen</p>
            <div className="flex flex-wrap gap-3">
              {data.status === "active" && (
                <button
                  onClick={() => doAction("pause")}
                  data-testid="commuter-manage-pause"
                  className="inline-flex items-center gap-2 border border-brand-line bg-white px-4 py-2.5 text-sm font-medium hover:bg-neutral-50"
                >
                  <Pause className="h-4 w-4" /> Service pausieren
                </button>
              )}
              {(data.status === "paused" || data.status === "pending") && !disabled && (
                <button
                  onClick={() => doAction("resume")}
                  data-testid="commuter-manage-resume"
                  className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-4 py-2.5 text-sm font-medium hover:bg-[hsl(var(--brand-green-strong))]"
                >
                  <Play className="h-4 w-4" /> Wieder aktivieren
                </button>
              )}
              {!disabled && (
                <>
                  <button
                    onClick={() => doAction("unsubscribe", "Möchten Sie sich wirklich abmelden? Sie erhalten dann keine E-Mails mehr.")}
                    data-testid="commuter-manage-unsubscribe"
                    className="inline-flex items-center gap-2 border border-brand-line bg-white px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50"
                  >
                    Abmelden
                  </button>
                  <button
                    onClick={() => doAction("delete-request", "Möchten Sie Ihre gesamten Daten dauerhaft löschen lassen? Diese Aktion ist endgültig.")}
                    data-testid="commuter-manage-delete"
                    className="inline-flex items-center gap-2 border border-red-200 bg-white px-4 py-2.5 text-sm text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" /> Daten löschen lassen
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Formular */}
          <div className="bg-white border border-brand-line p-6 sm:p-8 space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-dark))] mb-2">Ihre Daten</p>
              <h2 className="text-xl font-semibold">Strecke & Zeiten anpassen</h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Vorname</label>
                <input
                  type="text"
                  value={data.first_name || ""}
                  onChange={(e) => setData({ ...data, first_name: e.target.value })}
                  disabled={disabled}
                  data-testid="commuter-manage-first-name"
                  className="w-full bg-white border border-brand-line px-3.5 py-2.5 text-sm disabled:bg-neutral-50"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Nachname</label>
                <input
                  type="text"
                  value={data.last_name || ""}
                  onChange={(e) => setData({ ...data, last_name: e.target.value })}
                  disabled={disabled}
                  data-testid="commuter-manage-last-name"
                  className="w-full bg-white border border-brand-line px-3.5 py-2.5 text-sm disabled:bg-neutral-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Startadresse</label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
                <input
                  type="text"
                  value={data.start_address || ""}
                  onChange={(e) => setData({ ...data, start_address: e.target.value })}
                  disabled={disabled}
                  data-testid="commuter-manage-start"
                  className="w-full bg-white border border-brand-line pl-10 pr-3 py-2.5 text-sm disabled:bg-neutral-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Zieladresse</label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
                <input
                  type="text"
                  value={data.destination_address || ""}
                  onChange={(e) => setData({ ...data, destination_address: e.target.value })}
                  disabled={disabled}
                  data-testid="commuter-manage-dest"
                  className="w-full bg-white border border-brand-line pl-10 pr-3 py-2.5 text-sm disabled:bg-neutral-50"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-[180px,1fr] gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Abfahrtszeit</label>
                <Time24Picker
                  value={data.departure_time || "07:30"}
                  onChange={(v) => setData({ ...data, departure_time: v })}
                  disabled={disabled}
                  testId="commuter-manage-departure"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Wochentage</label>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map((d) => {
                    const active = (data.weekdays || []).includes(d.value);
                    return (
                      <button
                        type="button"
                        key={d.value}
                        onClick={() => !disabled && toggleDay(d.value)}
                        disabled={disabled}
                        data-testid={`commuter-manage-day-${d.value}`}
                        aria-pressed={active}
                        title={d.long}
                        className={`h-11 w-12 text-sm font-medium border transition-colors disabled:opacity-50 ${
                          active
                            ? "bg-[hsl(var(--brand-green-dark))] text-white border-[hsl(var(--brand-green-dark))]"
                            : "bg-white text-neutral-700 border-brand-line hover:border-[hsl(var(--brand-green))]"
                        }`}
                      >
                        {d.short}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={saveChanges}
                disabled={saving || disabled}
                data-testid="commuter-manage-save"
                className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Änderungen speichern
              </button>
            </div>
          </div>

          {/* Letzter Versand */}
          {data.last_sent_at && (
            <div className="bg-white border border-brand-line p-5 text-sm text-neutral-600 flex items-center gap-3" data-testid="commuter-manage-last-sent">
              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
              <span>
                Letzte Mail versendet: <strong className="text-neutral-900">{new Date(data.last_sent_at).toLocaleString("de-DE")}</strong>
                {data.last_route_status && <> · Status: {data.last_route_status}</>}
              </span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
